import React from 'react';
import {
  BannerActions,
  BannerButton,
  BannerRoot,
  BannerTitle,
  ReconnectingSpinner,
} from './SseConnectionLostBanner.styled';

export interface SseConnectionLostBannerProps {
  /**
   * True while the hook is between attempts (waiting on the backoff
   * timer or in the middle of opening the next stream). Drives the
   * "Reconnecting…" copy and the inline spinner so the user can tell
   * the client is still trying.
   */
  readonly isReconnecting: boolean;
  /**
   * Triggers a hard reload. Page refresh is the user-facing recovery
   * path because it re-syncs the React Query cache, the per-node
   * snapshot, AND any in-progress test-run state from the detail
   * endpoint -- which is the source of truth for everything the SSE
   * stream would have replayed in the old design.
   */
  readonly onReload: () => void;
  readonly dataTestId?: string;
}

/**
 * Banner shown when the live SSE connection to a process run drops and
 * the auto-reconnect loop is unable to immediately reattach.
 *
 * <p>Replaces the old replay-from-eventId mechanism: instead of trying
 * to backfill missed envelopes (which required a server-side audit log
 * and a fragile {@code afterEventId} contract), we tell the user the
 * stream is degraded and offer a refresh that re-fetches the canonical
 * persisted state. The reconnect-with-backoff loop in
 * {@code useProcessRunSession} keeps retrying in the background -- the
 * banner just surfaces the state so the user knows what's happening
 * and has an explicit recovery affordance.</p>
 */
const SseConnectionLostBanner: React.FC<SseConnectionLostBannerProps> = ({
  isReconnecting,
  onReload,
  dataTestId = 'sse-connection-lost-banner',
}) => (
  <BannerRoot
    role="status"
    aria-live="polite"
    aria-label="Live updates lost"
    data-testid={dataTestId}
  >
    {isReconnecting ? (
      <ReconnectingSpinner aria-hidden="true" />
    ) : null}
    <span>
      <BannerTitle>Connection lost.</BannerTitle>{' '}
      {isReconnecting
        ? 'Trying to reconnect — please refresh the page if updates do not resume.'
        : 'Live updates are unavailable. Please refresh the page to resync.'}
    </span>
    <BannerActions>
      <BannerButton
        type="button"
        onClick={onReload}
        data-testid={`${dataTestId}__refresh`}
      >
        Refresh page
      </BannerButton>
    </BannerActions>
  </BannerRoot>
);

export default SseConnectionLostBanner;
